/**
 * Notification & Response — 通知ポリシー BFF Contract
 *
 * BE SSoT:
 * `dresscode-backend/v2/src/shared-kernel/api/operation-engine/package/notification-and-response/notification-and-response.api-contract.ts`
 *
 * `@standandforce/api-contract` 未 publish のためローカル定義。
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './pagination';
const API_PREFIX = '/v2/operation-package/notification-and-response';
const NotificationPolicyStatusSchema = z.enum(['Draft', 'Active', 'Archived']);
const NotificationTaskKindSchema = z.enum(['FormInput', 'AutoProcess', 'Submit', 'Approval', 'Confirmation', 'Checklist', 'Receive']);
const NotificationEventKindSchema = z.enum(['Started', 'Finished', 'Approved', 'Rejected', 'Returned', 'Failed', 'Deadline']);
const PackageAssignmentOwnerProductSchema = z.enum(['HRF_LABOR_CORE', 'HRF_CONTRACTOR_CORE', 'IT_OPERATION_CORE', 'GA_OPERATION_CORE', 'STACKTRACE_PN', 'STACKTRACE_QA']);
const PackageAssignmentForceSchema = z.enum(['IT', 'HR', 'GA']);
const MultilingualLabelSchema = z.object({
  en: z.string(),
  ja: z.string(),
  id: z.string(),
  vn: z.string(),
  th: z.string(),
  zhCN: z.string()
});
const PolicyTriggerSchema = z.object({
  eventKind: NotificationEventKindSchema,
  enabled: z.boolean(),
  recipients: z.array(z.string()),
  message: z.object({
    subject: z.string().nullable(),
    body: z.string().nullable()
  }),
  deadlineLeadDays: z.number().int().nullable()
});
const PolicySettingSchema = z.object({
  taskKind: NotificationTaskKindSchema,
  triggers: z.array(PolicyTriggerSchema)
});
const PolicySummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: NotificationPolicyStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string()
});
const PolicyDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: NotificationPolicyStatusSchema,
  settings: z.array(PolicySettingSchema),
  createdAt: z.string(),
  updatedAt: z.string()
});
const PolicySettingInputSchema = z.object({
  taskKind: NotificationTaskKindSchema,
  triggers: z.array(PolicyTriggerSchema)
});
const CreatePolicyInputSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  settings: z.array(PolicySettingInputSchema)
}).strict();
const UpdatePolicyInputSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  settings: z.array(PolicySettingInputSchema)
}).strict();
const PolicyListQuerySchema = paginationQuerySchema(z.enum(['name', 'createdAt', 'updatedAt'])).extend({
  status: NotificationPolicyStatusSchema.optional()
});
const AssignablePolicyRefSchema = z.object({
  id: z.string(),
  name: z.string()
});
const PackageAssignmentItemSchema = z.object({
  operationPackageId: z.string(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  ownerProduct: PackageAssignmentOwnerProductSchema,
  force: PackageAssignmentForceSchema.nullable(),
  assignedPolicy: AssignablePolicyRefSchema.nullable()
});
const PackageAssignmentListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(2000).default(200),
  sortKey: z.enum(['name', 'ownerProduct']).optional(),
  order: z.enum(['asc', 'desc']).default('asc'),
  searchText: z.string().optional(),
  force: PackageAssignmentForceSchema.optional(),
  ownerProduct: PackageAssignmentOwnerProductSchema.optional()
});
const PutPackageAssignmentInputSchema = z.object({
  operationPackageId: z.string().min(1),
  notificationPolicyId: z.string().nullable()
}).strict();
const RecipientOptionSchema = z.object({
  lexeme: z.string(),
  label: MultilingualLabelSchema
});
const NotificationVariableSchema = z.object({
  name: z.string(),
  description: z.string()
});
export const notificationAndResponsePoliciesContract = oc.router({
  list: oc.route({
    method: 'GET',
    path: `${API_PREFIX}/policies`
  }).input(PolicyListQuerySchema).output(paginatedSchema(PolicySummarySchema)),
  get: oc.route({
    method: 'GET',
    path: `${API_PREFIX}/policies/{id}`
  }).input(z.object({
    id: z.string().min(1)
  })).output(PolicyDetailSchema),
  create: oc.route({
    method: 'POST',
    path: `${API_PREFIX}/policies`
  }).input(CreatePolicyInputSchema).output(PolicyDetailSchema),
  update: oc.route({
    method: 'PUT',
    path: `${API_PREFIX}/policies/{id}`
  }).input(UpdatePolicyInputSchema).output(PolicyDetailSchema),
  delete: oc.route({
    method: 'DELETE',
    path: `${API_PREFIX}/policies/{id}`
  }).input(z.object({
    id: z.string().min(1)
  })).output(z.object({
    id: z.string()
  })),
  assignable: oc.route({
    method: 'GET',
    path: `${API_PREFIX}/assignable-policies`
  }).input(z.object({}).strict()).output(z.array(AssignablePolicyRefSchema))
});
export const notificationAndResponsePackageAssignmentsContract = oc.router({
  list: oc.route({
    method: 'GET',
    path: `${API_PREFIX}/package-assignments`
  }).input(PackageAssignmentListQuerySchema).output(paginatedSchema(PackageAssignmentItemSchema)),
  put: oc.route({
    method: 'PUT',
    path: `${API_PREFIX}/package-assignments/{operationPackageId}`
  }).input(PutPackageAssignmentInputSchema).output(PackageAssignmentItemSchema)
});
export const notificationAndResponseRecipientOptionsContract = oc.router({
  list: oc.route({
    method: 'GET',
    path: `${API_PREFIX}/recipient-options`
  }).input(z.object({}).strict()).output(z.array(RecipientOptionSchema))
});
export const notificationAndResponseVariablesContract = oc.router({
  list: oc.route({
    method: 'GET',
    path: `${API_PREFIX}/variables`
  }).input(z.object({}).strict()).output(z.array(NotificationVariableSchema))
});
export type NotifResponsePolicySummary = z.infer<typeof PolicySummarySchema>;
export type NotifResponsePolicyDetail = z.infer<typeof PolicyDetailSchema>;
export type NotifResponsePolicySetting = z.infer<typeof PolicySettingSchema>;
export type NotifResponseAssignablePolicyRef = z.infer<typeof AssignablePolicyRefSchema>;
export type NotifResponsePackageAssignmentItem = z.infer<typeof PackageAssignmentItemSchema>;
export type NotifResponseRecipientOption = z.infer<typeof RecipientOptionSchema>;